import React, { useCallback, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FileUploadBox from '../../../buttons/FileUploadBox';
import UploadDocumentStatusButton from '../../../buttons/UploadDocumentStatusButton';
import PasswordField from '../../../fields/PasswordField';
import useSelectedCompany from '../../../../hooks/useSelectedCompany';
import useSubmitOfferCartOperation from '../../../../hooks/useSubmitOfferCartOperation';
import UPLOAD_CERTIFICATE from '../../../../graphql/mutations/upload-certificate';
import ShoppingCartStepperNavigation from '../../ShoppingCartStepperNavigation';
import InputHelperText from '../../../helper-texts/InputHelperText';
import ErrorBox from '../../../boxes/ErrorBox';
import ShoppingCartSummaryActionButton from '../../ShoppingCartSummary/ShoppingCartSummaryActionButton';
import { getDigitalCertificateUploadErrorMessage } from '../../../../constants/profile/credentials';

const UploadCertificateStep = ({
  prevStep,
  nextStep,
  selectedProvider,
  onCertificateUploaded,
  requireBankAccount,
}) => {
  const company = useSelectedCompany();
  const { id: companyId, hasDigitalCertificate } = company;
  const [uploadCertificate] = useMutation(UPLOAD_CERTIFICATE);
  const { submitOperation } = useSubmitOfferCartOperation({
    certificatePassword: '',
    isExternal: false,
  });
  const [certificateFile, setCertificateFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];
    setCertificateFile(file);
  }, []);

  const handleCancel = useCallback(() => {
    setCertificateFile(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  const handleSubmit = useCallback(
    (values, { setSubmitting, setFieldError }) => {
      if (!certificateFile && !hasDigitalCertificate) {
        setFieldError(
          'certificateFile',
          'El certificado digital es obligatorio',
        );
        setSubmitting(false);
        return;
      }
      const afterCertificateReady = () => {
        if (requireBankAccount) {
          setSubmitting(false);
          nextStep();
          return;
        }
        submitOperation({
          digitalCertificatePassword: values.password,
          onCompleted: () => {
            setSubmitting(false);
            nextStep();
          },
          onError: (errorMessage) => {
            setFieldError('submit', errorMessage);
            setSubmitting(false);
          },
        });
      };

      setUploading(true);
      if (hasDigitalCertificate) {
        onCertificateUploaded({ password: values.password });
        setUploading(false);
        afterCertificateReady();
        return;
      }
      uploadCertificate({
        variables: {
          certificatePassword: values.password,
          digitalCertificate: certificateFile,
          companyId,
          credentialProviderName: selectedProvider?.name,
        },
        onCompleted: () => {
          onCertificateUploaded({ password: values.password });
          setUploading(false);
          afterCertificateReady();
        },
        onError: (error) => {
          setFieldError('submit', getDigitalCertificateUploadErrorMessage(error));
          setUploading(false);
          setSubmitting(false);
        },
      });
    },
    [
      hasDigitalCertificate,
      uploadCertificate,
      companyId,
      selectedProvider,
      nextStep,
      onCertificateUploaded,
      certificateFile,
      requireBankAccount,
      submitOperation,
    ],
  );

  const initialValues = useMemo(
    () => ({
      password: '',
    }),
    [],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object({
        password: Yup.string().required('La contraseña es obligatoria'),
      })}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting, isValid }) => (
        <Form style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <Stack direction="column" spacing={2}>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'gray.A300',
                borderRadius: 1.5,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FileUploadBox
                onFileChange={handleFileChange}
                acceptedFiles=".pfx"
                inputRef={inputRef}
              >
                <UploadDocumentStatusButton
                  hasInvoiceDocuments={!!certificateFile || hasDigitalCertificate}
                  documentName={certificateFile?.name}
                  file={certificateFile}
                  onCancel={handleCancel}
                  loading={uploading}
                  initialText="Adjuntar"
                  finalText=" certificado digital"
                  uploadedText="Certificado digital cargado"
                />
              </FileUploadBox>
            </Box>
            {touched.certificateFile && errors.certificateFile && (
            <InputHelperText error>
              {errors.certificateFile}
            </InputHelperText>
            )}
            <PasswordField
              placeholder="Contraseña"
              InputProps={{
                inputProps: { sx: { paddingTop: '0px' } },
                sx: {
                  border: '1px solid',
                  borderColor: 'gray.A400',
                  backgroundColor: 'white',
                  fontSize: '14px',
                },
              }}
            />
            {errors.submit && <ErrorBox>{errors.submit}</ErrorBox>}
            <ShoppingCartStepperNavigation onBack={prevStep}>
              <ShoppingCartSummaryActionButton
                id="confirm-upload-certificate-button"
                type="submit"
                label={(
                  <Typography variant="small_3" color="white" fontWeight={600}>
                    {requireBankAccount ? 'Continuar' : 'Confirmar'}
                  </Typography>
              )}
                size="small"
                loading={isSubmitting || uploading}
                disabled={
                (!hasDigitalCertificate && !certificateFile)
                || !isValid
                || isSubmitting
                || uploading
              }
              />
            </ShoppingCartStepperNavigation>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

UploadCertificateStep.propTypes = {
  prevStep: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  selectedProvider: PropTypes.shape({
    name: PropTypes.string,
  }),
  onCertificateUploaded: PropTypes.func.isRequired,
  requireBankAccount: PropTypes.bool,
};

UploadCertificateStep.defaultProps = {
  selectedProvider: null,
  requireBankAccount: true,
};

export default UploadCertificateStep;
